import type { PositionDetail, TradeItem } from '@/types/trades.types';

/**
 * LR-628: the rate a trade is ACTUALLY accruing at today, vs the one it booked.
 *
 * A floating rebate reprices its benchmark from that day's fixing every night,
 * so the booked `benchmark_rate` / `total_rate` stop describing what the
 * statement bills the moment the market moves. The backend keeps the booked pair
 * as the record (audit trail, re-rate "before" values, the payload an amend
 * posts back) and serves the moving figures alongside as `effective_*`, null for
 * everything that does not move — fixed rates, repo, and benchmarks with no
 * published feed.
 *
 * LR-631 added the second thing that moves: a fee trade's cost leg accrues at
 * the rate of the cash pool holding its collateral (fixing plus the pool's
 * spread, or a flat fixed pool rate), so a POOLED fee trade serves `effective_*`
 * too. A fee trade with no pool still does not.
 *
 * So the rule is one line, and it lives here rather than at five call sites:
 * prefer the effective figure, fall back to the booked one.
 */
export function effectiveRates(trade: PositionDetail) {
  return {
    benchmarkRate: trade.effective_benchmark_rate ?? trade.benchmark_rate,
    totalRate: trade.effective_total_rate ?? trade.total_rate,
    dailyEarnings: trade.effective_daily_earnings ?? trade.daily_earnings,
    annualEarnings: trade.effective_annual_earnings ?? trade.annual_earnings,
    // The settlement twin moves with its own original: on a cross-currency trade
    // the CONVERTED figure is the hero and the security-ccy one the "≈" beside
    // it, so mixing bases would put two different rates in one line.
    dailyEarningsConverted:
      trade.effective_daily_earnings_converted ?? trade.daily_earnings_converted,
    /** True when the figures above came from the rate the accrual is USING NOW
     *  — the resolved fixing, or the cash pool's current definition (LR-631) —
     *  rather than from the booking snapshot. Deliberately NOT a synonym for the
     *  payload's
     *  `benchmark_follows_fixing`: the two differ exactly on the case that
     *  matters, a fixing-driven trade whose feed is down — the accrual still owns
     *  its benchmark (so the re-rate form still withholds the field) while what
     *  is on screen is the booked number. Use this to LABEL a figure, that one to
     *  decide whether a human may set it.
     *
     *  LR-656: it does NOT say the fixing was TAKEN today, and no caption built on
     *  it may. `latest_fixings` carries the last good print forward for up to
     *  MAX_FIXING_STALENESS_DAYS (7) and drops the `as_of` it resolved on, so the
     *  payload holds no fixing date at all — over 30 days on treasury9, sonia sat
     *  on a carried print 17 working days out of 22. Hence "live rate" rather than
     *  "today's fixing": the old name is what put the word "today" on three
     *  screens, and a name is where the next caption will be read from.
     *
     *  Keyed on the benchmark, not the total: once the pool accrues the cash the
     *  total is the fee alone and is served whether or not the pool's rate is,
     *  so a non-null total no longer says the benchmark beside it is live. */
    showingLiveRate: trade.effective_benchmark_rate != null,
  };
}

/**
 * The same rule over the blotter's numeric row shape (`TradeItem`). Same fields,
 * same fallback — the mapper parses the `effective_*` strings alongside their
 * booked twins, so the only difference is the type.
 *
 * The provenance flag lives in both shapes (!348 brought it here for the bulk
 * grid) under ONE name, `showingLiveRate`, with the contract documented on the
 * twin above — two names for one question is how the blotter and the trade page
 * end up captioning the same number two ways.
 */
export function effectiveTradeRates(trade: TradeItem) {
  return {
    benchmarkRate: trade.effective_benchmark_rate ?? trade.benchmark_rate,
    totalRate: trade.effective_total_rate ?? trade.total_rate,
    dailyEarnings: trade.effective_daily_earnings ?? trade.daily_earnings,
    // LR-652: same rule and same key as the twin above — a caller that LABELS one
    // of these figures needs the flag wherever it reads them, and two functions
    // deriving "is this the live rate" from two different fields is how the blotter
    // and the trade page end up captioning the same number differently.
    // LR-656 renamed it off "today's fixing": the accrual resolves through a
    // carry-forward window and the payload carries no `as_of`, so neither shape
    // may date the figure. The grid's own caption already said "fixing" — this is
    // the name catching up with it.
    showingLiveRate: trade.effective_benchmark_rate != null,
  };
}
